// ClientPortalPage — landing rendered at /cliente/:token. NO auth.
//
// Tabs:
//   - Overview     → welcome card + agent contact + counts + quick actions
//   - Policies     → list of all the client's policies
//   - Documents    → docs the agent uploaded for this client
//   - Signatures   → pending + completed signature requests, download signed PDF
//   - Quotes       → active quotes with the same public landing reusable
//   - Invoices     → invoices with totals
//
// All data fetched via /portal/:token/* (no JWT, no MSAL). El enlace solo no
// basta: el servidor pide los 4 últimos del SSN (Lissette 2026-10-19) y hasta
// acertarlos no enseña nada del cliente. Mismo candado que el enlace de
// renovación (`CandadoDelSsn.jsx`).

import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Shield, FileText, FileSignature, Receipt, Calculator, User,
  Phone, Mail, Calendar, Download, ExternalLink, AlertCircle,
  Loader2, CheckCircle, Clock, X, Eye,
} from "lucide-react";
import {
  getPortalOverview, getPortalPolicies, getPortalDocuments,
  getPortalSignatures, getPortalQuotes, getPortalInvoices,
} from "../api.js";
import { calendarDayOf } from "../utils.js";
import { CandadoDelSsn } from "./CandadoDelSsn.jsx";

const TABS = [
  { id: "overview",   icon: User,           labelEs: "Inicio",        labelEn: "Overview" },
  { id: "policies",   icon: Shield,         labelEs: "Pólizas",       labelEn: "Policies" },
  { id: "documents",  icon: FileText,       labelEs: "Documentos",    labelEn: "Documents" },
  { id: "signatures", icon: FileSignature,  labelEs: "Firmas",        labelEn: "Signatures" },
  { id: "quotes",     icon: Calculator,     labelEs: "Cotizaciones",  labelEn: "Quotes" },
  { id: "invoices",   icon: Receipt,        labelEs: "Facturas",      labelEn: "Invoices" },
];

export function ClientPortalPage({ token, lang: initialLang }) {
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  // El candado: mientras el servidor diga `locked`, se piden los 4 últimos del
  // SSN. El código bueno se queda SOLO en memoria (recargar lo vuelve a pedir)
  // y viaja con cada pestaña, porque el servidor lo pide en todas.
  const [bloqueo, setBloqueo] = useState(null);
  const [codigo, setCodigo] = useState("");
  const [verificando, setVerificando] = useState(false);

  // Lang preference: detect from query, then from overview.client.preferredLanguage,
  // then from initialLang prop. Sticky once set so reload preserves it.
  const [lang, setLang] = useState(() => {
    if (typeof window === "undefined") return initialLang || "en";
    const url = new URLSearchParams(window.location.search);
    return url.get("lang") || initialLang || "en";
  });
  const t = (es, en) => (lang === "es" ? es : en);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const ov = await getPortalOverview(token);
      if (ov?.locked) {
        setBloqueo(ov);
        if (!new URLSearchParams(window.location.search).get("lang") && ov.preferredLanguage) {
          setLang(ov.preferredLanguage);
        }
        return;
      }
      setOverview(ov);
      // Auto-detect language from client preference if URL didn't override
      if (!new URLSearchParams(window.location.search).get("lang")
          && ov?.client?.preferredLanguage) {
        setLang(ov.client.preferredLanguage);
      }
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  async function desbloquear(intento) {
    if (verificando) return;
    setVerificando(true);
    try {
      const ov = await getPortalOverview(token, intento);
      if (ov?.locked) { setBloqueo(ov); return; }
      setCodigo(intento);
      setBloqueo(null);
      setOverview(ov);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setVerificando(false);
    }
  }

  if (loading) return <FullPageLoading />;
  if (error)   return <FullPageError error={error} lang={lang} />;
  if (bloqueo) return <CandadoDelSsn bloqueo={bloqueo} t={t} verificando={verificando} onProbar={desbloquear} />;
  if (!overview) return null;

  const brand = "#00d4ff";

  return (
    <div style={{
      minHeight: "100vh",
      background: `linear-gradient(180deg, ${brand}11 0%, #f4f7fb 280px)`,
      fontFamily: "system-ui,-apple-system,Segoe UI,sans-serif",
      color: "#0a1628",
    }}>
      {/* Header */}
      <header style={{
        background: "#fff", borderBottom: "1px solid #e1e8f0",
        padding: ".9rem 1.25rem",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        boxShadow: "0 1px 3px rgba(0,0,0,.04)",
      }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: "1.05rem", color: "#0a1628" }}>
            {overview.agency?.name || "Deskte"}
          </div>
          <div style={{ fontSize: ".7rem", color: "#5b7a9a" }}>
            {t("Portal del cliente", "Client portal")}
          </div>
        </div>
        <button onClick={() => { const next = lang === "es" ? "en" : "es"; setLang(next); }} style={{
          padding: ".45rem .85rem", borderRadius: 999,
          background: "#f4f7fb", color: "#3a5a7a",
          border: "1px solid #e1e8f0", cursor: "pointer", fontWeight: 700, fontSize: ".75rem",
        }}>
          {lang === "es" ? "🇺🇸 EN" : "🇪🇸 ES"}
        </button>
      </header>

      {/* Welcome banner */}
      <div style={{ padding: "1.25rem 1.25rem 0", maxWidth: 960, margin: "0 auto" }}>
        <h1 style={{ margin: 0, fontSize: "1.5rem", color: "#0a1628" }}>
          {t("Hola", "Hi")}, {overview.client.firstName} 👋
        </h1>
        <p style={{ margin: ".4rem 0 1.25rem", color: "#5b7a9a", fontSize: ".92rem" }}>
          {t(
            "Aquí tienes tus pólizas, documentos, firmas, cotizaciones y facturas en un solo lugar.",
            "Here are your policies, documents, signatures, quotes and invoices — all in one place.",
          )}
        </p>
      </div>

      {/* Tab nav */}
      <div style={{
        position: "sticky", top: 0, zIndex: 5,
        background: "#fff", borderBottom: "1px solid #e1e8f0",
      }}>
        <div style={{
          maxWidth: 960, margin: "0 auto", padding: "0 1.25rem",
          display: "flex", gap: 4, overflowX: "auto",
        }}>
          {TABS.map((tb) => {
            const isActive = tab === tb.id;
            const count = overview.counts?.[tb.id];
            return (
              <button key={tb.id} onClick={() => setTab(tb.id)} style={{
                padding: ".85rem 1rem",
                background: "transparent",
                border: "none",
                borderBottom: isActive ? `2px solid ${brand}` : "2px solid transparent",
                color: isActive ? "#0a1628" : "#5b7a9a",
                fontWeight: isActive ? 700 : 600,
                cursor: "pointer", fontSize: ".88rem",
                display: "inline-flex", alignItems: "center", gap: 6,
                whiteSpace: "nowrap", flexShrink: 0,
              }}>
                <tb.icon size={14} />
                {lang === "es" ? tb.labelEs : tb.labelEn}
                {typeof count === "number" && count > 0 && (
                  <span style={{
                    background: isActive ? brand : "#e1e8f0",
                    color: isActive ? "#0a1628" : "#5b7a9a",
                    padding: "1px 7px", borderRadius: 999,
                    fontSize: ".7rem", fontWeight: 700,
                  }}>{count}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab body */}
      <div style={{ padding: "1.25rem", maxWidth: 960, margin: "0 auto" }}>
        {tab === "overview"   && <OverviewPanel   overview={overview} lang={lang} onTabChange={setTab} />}
        {tab === "policies"   && <PoliciesPanel   token={token} codigo={codigo} lang={lang} />}
        {tab === "documents"  && <DocumentsPanel  token={token} codigo={codigo} lang={lang} />}
        {tab === "signatures" && <SignaturesPanel token={token} codigo={codigo} lang={lang} />}
        {tab === "quotes"     && <QuotesPanel     token={token} codigo={codigo} lang={lang} />}
        {tab === "invoices"   && <InvoicesPanel   token={token} codigo={codigo} lang={lang} />}
      </div>

      {/* Footer */}
      <div style={{ textAlign: "center", padding: "2rem 1rem 1rem", fontSize: ".7rem", color: "#7d97b3" }}>
        {t("Tus datos están protegidos. Solo tú y tu agente pueden verlos.",
           "Your data is protected. Only you and your agent can see it.")} · powered by <strong>Deskte</strong>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// Overview
// ----------------------------------------------------------------------

function OverviewPanel({ overview, lang, onTabChange }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const { client, agent, counts } = overview;
  const agencyLogo = overview.agency?.logoUrl || null;
  const agencyName = overview.agency?.name || "";

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: "1rem" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        {/* Quick stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: ".75rem" }}>
          <StatCard icon={Shield}        n={counts.policies}   label={t("Pólizas",      "Policies")}   onClick={() => onTabChange("policies")} />
          <StatCard icon={FileText}      n={counts.documents}  label={t("Documentos",   "Documents")}  onClick={() => onTabChange("documents")} />
          <StatCard icon={FileSignature} n={counts.signatures} label={t("Firmas",       "Signatures")} onClick={() => onTabChange("signatures")} />
          <StatCard icon={Calculator}    n={counts.quotes}     label={t("Cotizaciones", "Quotes")}     onClick={() => onTabChange("quotes")} />
          <StatCard icon={Receipt}       n={counts.invoices}   label={t("Facturas",     "Invoices")}   onClick={() => onTabChange("invoices")} />
        </div>

        {/* Profile card */}
        <PortalCard title={t("Tu información", "Your info")}>
          <Row icon={User}     label={t("Nombre", "Name")}    value={`${client.firstName} ${client.lastName}`} />
          {client.email && <Row icon={Mail}  label="Email" value={client.email} link={`mailto:${client.email}`} />}
          {client.phone && <Row icon={Phone} label={t("Teléfono", "Phone")} value={client.phone} link={`tel:${client.phone}`} />}
        </PortalCard>
      </div>

      {/* Agent contact card */}
      <div>
        <PortalCard title={t("Tu agente", "Your agent")}>
          <div style={{ textAlign: "center", padding: ".5rem 0 .75rem" }}>
            {/* Logo de la agencia primero (Lissette 2026-10-22); si no hay,
                la foto del agente; si tampoco, la inicial. */}
            {agencyLogo ? (
              <img src={agencyLogo} alt={agencyName || ""} style={{ display: "block", maxWidth: 180, maxHeight: 72, objectFit: "contain", margin: "0 auto" }} />
            ) : agent?.avatarUrl ? (
              <img src={agent.avatarUrl} alt="" style={{ width: 72, height: 72, borderRadius: "50%", objectFit: "cover", margin: "0 auto" }} />
            ) : (
              <div style={{
                width: 72, height: 72, borderRadius: "50%", margin: "0 auto",
                background: "#00d4ff22", color: "#00a8c8", border: "2px solid #00d4ff66",
                display: "grid", placeItems: "center", fontSize: "1.5rem", fontWeight: 800,
              }}>
                {(agent?.displayName || "A").charAt(0)}
              </div>
            )}
            <div style={{ fontWeight: 800, marginTop: 10, fontSize: "1rem", color: "#0a1628" }}>
              {agent?.displayName || t("Tu agente", "Your agent")}
            </div>
          </div>
          {agent?.email && (
            <a href={`mailto:${agent.email}`} style={contactBtnStyle("primary")}>
              <Mail size={14} /> {agent.email}
            </a>
          )}
          {agent?.phone && (
            <a href={`tel:${agent.phone}`} style={contactBtnStyle("ghost")}>
              <Phone size={14} /> {agent.phone}
            </a>
          )}
        </PortalCard>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, n, label, onClick }) {
  return (
    <button onClick={onClick} style={{
      background: "#fff", border: "1px solid #e1e8f0", borderRadius: 12,
      padding: "1rem", cursor: "pointer", textAlign: "left",
      display: "flex", flexDirection: "column", gap: 4,
      transition: "border-color .15s, transform .1s",
    }}
    onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#00d4ff66"; }}
    onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e1e8f0"; }}>
      <Icon size={18} color="#00a8c8" />
      <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "#0a1628" }}>{n}</div>
      <div style={{ fontSize: ".78rem", color: "#5b7a9a", fontWeight: 600 }}>{label}</div>
    </button>
  );
}

// ----------------------------------------------------------------------
// Policies
// ----------------------------------------------------------------------

function PoliciesPanel({ token, codigo, lang }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    getPortalPolicies(token, codigo).then((d) => (d?.locked ? setError(lang === "es" ? "Vuelve a abrir el enlace para verificar tu identidad." : "Open the link again to verify your identity.") : setData(d))).catch((e) => setError(e.message));
  }, [token, codigo]);
  if (error) return <ErrorPanel text={error} />;
  if (!data) return <Spinner lang={lang} />;
  if (data.length === 0) return <EmptyPanel text={t("Aún no tienes pólizas registradas.", "No policies yet.")} />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: ".75rem" }}>
      {data.map((p) => (
        <PortalCard key={p.id}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "inline-block", background: "#00d4ff22", color: "#00a8c8", padding: "2px 9px", borderRadius: 999, fontSize: ".68rem", fontWeight: 700, letterSpacing: ".05em", marginBottom: 6 }}>
                {p.type}
              </div>
              {/* El NOMBRE DEL PLAN manda cuando lo hay: es lo que el cliente
                  reconoce de su tarjeta. La compania queda debajo, mas
                  pequena, porque contesta otra pregunta. Sin plan escrito, la
                  compania sube al titulo y no queda un hueco raro. */}
              <div style={{ fontSize: "1.05rem", fontWeight: 700, color: "#0a1628" }}>{p.planName || p.carrier}</div>
              {p.planName && p.carrier && (
                <div style={{ fontSize: ".82rem", color: "#5b7a9a", marginTop: 1 }}>{p.carrier}</div>
              )}
              {p.policyNumber && (
                <div style={{ fontSize: ".78rem", color: "#5b7a9a", fontFamily: "var(--font-mono,monospace)", marginTop: 2 }}>
                  #{p.policyNumber}
                </div>
              )}
            </div>
            <div style={{
              fontSize: ".7rem", padding: "3px 9px", borderRadius: 999, fontWeight: 700, letterSpacing: ".04em",
              background: p.status === "ACTIVE" ? "#10b98122" : "#94a3b822",
              color: p.status === "ACTIVE" ? "#10b981" : "#5b7a9a",
            }}>{p.status}</div>
          </div>
          <Divider />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: ".5rem .75rem" }}>
            <KV label={t("Prima", "Premium")} value={`$${Number(p.premium || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${p.premiumFrequency ? ` / ${String(p.premiumFrequency).toLowerCase()}` : ''}`} />
            <KV label={t("Vigencia", "Effective")} value={fmtDay(p.effectiveDate, lang)} />
            {p.expirationDate && <KV label={t("Renovación", "Renews")} value={fmtDay(p.expirationDate, lang)} />}
            {p.coverageAmount && <KV label={t("Cobertura", "Coverage")} value={`$${Number(p.coverageAmount).toLocaleString('en-US')}`} />}
            {p.deductible && <KV label={t("Deducible", "Deductible")} value={`$${Number(p.deductible).toLocaleString('en-US')}`} />}
          </div>
          {/* Quien esta cubierto por ESTA poliza. Es la pregunta que el cliente
              hace por telefono — "¿mi hija esta en esta?" — y hasta ahora no
              habia forma de contestarla sin llamar al agente.
              Solo se pinta si hay a quien enseñar: una linea "Miembros:" vacia
              se lee como que no hay nadie cubierto, que seria mentira. Los
              nombres los arma el servidor; aqui no llega el registro familiar. */}
          {Array.isArray(p.members) && p.members.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: ".68rem", fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", color: "#5b7a9a", marginBottom: 5 }}>
                {t(p.members.length === 1 ? "Miembro" : "Miembros", p.members.length === 1 ? "Member" : "Members")}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {p.members.map((m, i) => (
                  <span key={`${m}-${i}`} style={{ background: "#eef4fb", color: "#3a5a7a", padding: "3px 10px", borderRadius: 999, fontSize: ".76rem", fontWeight: 600 }}>{m}</span>
                ))}
              </div>
            </div>
          )}
          {p.notes && (
            <div style={{ marginTop: 10, padding: ".6rem .8rem", background: "#f4f7fb", borderRadius: 8, fontSize: ".78rem", color: "#3a5a7a", lineHeight: 1.5 }}>
              {p.notes}
            </div>
          )}
        </PortalCard>
      ))}
    </div>
  );
}

// ----------------------------------------------------------------------
// Documents
// ----------------------------------------------------------------------

function DocumentsPanel({ token, codigo, lang }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    getPortalDocuments(token, codigo).then((d) => (d?.locked ? setError(lang === "es" ? "Vuelve a abrir el enlace para verificar tu identidad." : "Open the link again to verify your identity.") : setData(d))).catch((e) => setError(e.message));
  }, [token, codigo]);
  if (error) return <ErrorPanel text={error} />;
  if (!data) return <Spinner lang={lang} />;
  if (data.length === 0) return <EmptyPanel text={t("Aún no hay documentos disponibles.", "No documents available yet.")} />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: ".5rem" }}>
      {data.map((d) => (
        <PortalCard key={d.id} compact>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: "#00d4ff22", color: "#00a8c8",
              display: "grid", placeItems: "center", flexShrink: 0,
            }}>
              <FileText size={18} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, color: "#0a1628", fontSize: ".92rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {d.name}
              </div>
              <div style={{ fontSize: ".72rem", color: "#5b7a9a" }}>
                {d.category || d.type} · {fmtSize(d.sizeBytes)} · {fmtDate(d.uploadedAt, lang)}
              </div>
            </div>
            {d.blobUrl && (
              <a href={d.blobUrl} target="_blank" rel="noopener noreferrer" style={contactBtnStyle("ghost")}>
                <Download size={13} /> {t("Ver", "View")}
              </a>
            )}
          </div>
        </PortalCard>
      ))}
    </div>
  );
}

// ----------------------------------------------------------------------
// Signatures
// ----------------------------------------------------------------------

function SignaturesPanel({ token, codigo, lang }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    getPortalSignatures(token, codigo).then((d) => (d?.locked ? setError(lang === "es" ? "Vuelve a abrir el enlace para verificar tu identidad." : "Open the link again to verify your identity.") : setData(d))).catch((e) => setError(e.message));
  }, [token, codigo]);
  if (error) return <ErrorPanel text={error} />;
  if (!data) return <Spinner lang={lang} />;
  if (data.length === 0) return <EmptyPanel text={t("No tienes firmas pendientes ni completadas.", "No signature requests yet.")} />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: ".5rem" }}>
      {data.map((s) => {
        const isPending = s.status === "PENDING";
        const isSigned  = s.status === "SIGNED";
        // SECURITY: the client portal no longer surfaces the per-signature
        // signing token. The signing link is delivered out-of-band (the
        // email/SMS the agent originally sent), so a leaked portal token
        // grants read-only access — it can no longer be used to forge a
        // signature. If the client lost the email, they ask the agent to
        // resend, who can reissue via the authenticated dashboard.
        return (
          <PortalCard key={s.id}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: "#0a1628", fontSize: "1rem" }}>{s.documentName}</div>
                <div style={{ fontSize: ".75rem", color: "#5b7a9a", marginTop: 2 }}>
                  {isSigned
                    ? t(`Firmado el ${fmtDate(s.signedAt, lang)}`, `Signed ${fmtDate(s.signedAt, lang)}`)
                    : t(`Enviado el ${fmtDate(s.sentAt, lang)}`, `Sent ${fmtDate(s.sentAt, lang)}`)
                  }
                </div>
              </div>
              <div style={{
                fontSize: ".7rem", padding: "3px 9px", borderRadius: 999, fontWeight: 700, letterSpacing: ".04em",
                background: isSigned ? "#10b98122" : isPending ? "#f59e0b22" : "#94a3b822",
                color:      isSigned ? "#10b981"   : isPending ? "#f59e0b"   : "#5b7a9a",
                display: "inline-flex", alignItems: "center", gap: 4,
              }}>
                {isSigned ? <CheckCircle size={11} /> : <Clock size={11} />}
                {s.status}
              </div>
            </div>
            {isPending && (
              <div style={{
                marginTop: 10, padding: ".55rem .8rem", borderRadius: 9,
                background: "#fffbeb", border: "1px solid #fde68a",
                color: "#92400e", fontSize: ".78rem", lineHeight: 1.4,
              }}>
                <FileSignature size={13} style={{ display: "inline", marginRight: 5, verticalAlign: "text-bottom" }}/>
                {t("Revisa tu email para el enlace de firma. Si no lo encuentras, contacta a tu agente para que te lo reenvíe.",
                   "Check your email for the signing link. If you can't find it, ask your agent to resend.")}
              </div>
            )}
            {isSigned && s.signedDocUrl && (
              <a href={s.signedDocUrl} target="_blank" rel="noopener noreferrer" style={{
                marginTop: 10, display: "inline-flex", alignItems: "center", gap: 6,
                padding: ".5rem 1rem", borderRadius: 9,
                background: "#00d4ff", color: "#0a1628",
                fontWeight: 700, fontSize: ".82rem", textDecoration: "none",
              }}>
                <Download size={14} /> {t("Descargar PDF firmado", "Download signed PDF")}
              </a>
            )}
          </PortalCard>
        );
      })}
    </div>
  );
}

// ----------------------------------------------------------------------
// Quotes
// ----------------------------------------------------------------------

function QuotesPanel({ token, codigo, lang }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    getPortalQuotes(token, codigo).then((d) => (d?.locked ? setError(lang === "es" ? "Vuelve a abrir el enlace para verificar tu identidad." : "Open the link again to verify your identity.") : setData(d))).catch((e) => setError(e.message));
  }, [token, codigo]);
  if (error) return <ErrorPanel text={error} />;
  if (!data) return <Spinner lang={lang} />;
  if (data.length === 0) return <EmptyPanel text={t("Aún no tienes cotizaciones.", "No quotes yet.")} />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: ".75rem" }}>
      {data.map((q) => (
        <PortalCard key={q.id}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: ".72rem", color: "#5b7a9a", letterSpacing: ".04em", fontWeight: 700 }}>
                {q.number}
              </div>
              <div style={{ fontSize: "1.05rem", fontWeight: 700, color: "#0a1628", marginTop: 2 }}>{q.title}</div>
            </div>
            <div style={{
              fontSize: ".7rem", padding: "3px 9px", borderRadius: 999, fontWeight: 700, letterSpacing: ".04em",
              background: q.status === "ACCEPTED" ? "#10b98122" : q.status === "DECLINED" ? "#ef444422" : "#3b82f622",
              color:      q.status === "ACCEPTED" ? "#10b981"   : q.status === "DECLINED" ? "#ef4444"   : "#3b82f6",
            }}>{q.status}</div>
          </div>
          <Divider />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: ".82rem", color: "#5b7a9a" }}>
              {q.items?.length || 0} {t("opciones", "options")}
            </div>
            <div style={{ fontSize: "1.1rem", fontWeight: 800, color: "#0a1628" }}>
              ${Number(q.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
          {q.publicToken && (
            <a href={`${window.location.origin}/quote/${q.publicToken}`} target="_blank" rel="noopener noreferrer" style={contactBtnStyle("primary", true)}>
              <Eye size={13} /> {t("Ver cotización completa", "View full quote")} <ExternalLink size={11} />
            </a>
          )}
        </PortalCard>
      ))}
    </div>
  );
}

// ----------------------------------------------------------------------
// Invoices
// ----------------------------------------------------------------------

function InvoicesPanel({ token, codigo, lang }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    getPortalInvoices(token, codigo).then((d) => (d?.locked ? setError(lang === "es" ? "Vuelve a abrir el enlace para verificar tu identidad." : "Open the link again to verify your identity.") : setData(d))).catch((e) => setError(e.message));
  }, [token, codigo]);
  if (error) return <ErrorPanel text={error} />;
  if (!data) return <Spinner lang={lang} />;
  if (data.length === 0) return <EmptyPanel text={t("Aún no tienes facturas.", "No invoices yet.")} />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: ".75rem" }}>
      {data.map((inv) => {
        const balance = Number(inv.total) - Number(inv.amountPaid || 0);
        return (
          <PortalCard key={inv.id}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: ".72rem", color: "#5b7a9a", letterSpacing: ".04em", fontWeight: 700 }}>
                  {inv.number}
                </div>
                <div style={{ fontSize: "1.05rem", fontWeight: 700, color: "#0a1628", marginTop: 2 }}>{inv.title}</div>
              </div>
              <div style={{
                fontSize: ".7rem", padding: "3px 9px", borderRadius: 999, fontWeight: 700, letterSpacing: ".04em",
                background: inv.status === "PAID" ? "#10b98122" : inv.status === "OVERDUE" ? "#ef444422" : "#3b82f622",
                color:      inv.status === "PAID" ? "#10b981"   : inv.status === "OVERDUE" ? "#ef4444"   : "#3b82f6",
              }}>{inv.status}</div>
            </div>
            <Divider />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
              <KV label={t("Total",      "Total")}    value={`$${Number(inv.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
              <KV label={t("Pagado",     "Paid")}     value={`$${Number(inv.amountPaid || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
              <KV label={t("Pendiente",  "Balance")}  value={`$${Number(balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} highlight={balance > 0} />
            </div>
            {inv.dueDate && (
              <div style={{ fontSize: ".75rem", color: "#5b7a9a", marginTop: 8 }}>
                <Calendar size={11} style={{ verticalAlign: "-2px" }} /> {t("Vence", "Due")} {fmtDay(inv.dueDate, lang)}
              </div>
            )}
          </PortalCard>
        );
      })}
    </div>
  );
}

// ----------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------

function PortalCard({ title, children, compact }) {
  return (
    <div style={{
      background: "#fff", borderRadius: 12,
      padding: compact ? "0.75rem 1rem" : "1.25rem",
      boxShadow: "0 4px 12px rgba(0,0,0,.04)",
      border: "1px solid #e9eef5",
    }}>
      {title && (
        <div style={{
          fontSize: ".7rem", letterSpacing: ".06em", textTransform: "uppercase",
          color: "#7d97b3", fontWeight: 700, marginBottom: ".75rem",
        }}>{title}</div>
      )}
      {children}
    </div>
  );
}

function Row({ icon: Icon, label, value, link }) {
  const inner = (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0" }}>
      <Icon size={15} color="#7d97b3" style={{ flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: ".7rem", color: "#7d97b3", textTransform: "uppercase", letterSpacing: ".05em", fontWeight: 700 }}>{label}</div>
        <div style={{ fontSize: ".88rem", color: "#0a1628", fontWeight: 600 }}>{value}</div>
      </div>
    </div>
  );
  if (link) return <a href={link} style={{ textDecoration: "none", color: "inherit" }}>{inner}</a>;
  return inner;
}

function KV({ label, value, highlight }) {
  return (
    <div>
      <div style={{ fontSize: ".68rem", color: "#7d97b3", textTransform: "uppercase", letterSpacing: ".05em", fontWeight: 700, marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: ".92rem", color: highlight ? "#ef4444" : "#0a1628", fontWeight: 700 }}>
        {value}
      </div>
    </div>
  );
}

function Divider() {
  return <div style={{ height: 1, background: "#e9eef5", margin: "10px 0" }} />;
}

function contactBtnStyle(kind, fullWidth) {
  const map = {
    primary: { bg: "#00d4ff", color: "#0a1628", border: "transparent" },
    ghost:   { bg: "#f4f7fb", color: "#3a5a7a", border: "#e1e8f0" },
  }[kind];
  return {
    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
    padding: ".55rem .85rem", borderRadius: 9,
    background: map.bg, color: map.color,
    border: `1px solid ${map.border}`, textDecoration: "none",
    fontSize: ".82rem", fontWeight: 700,
    width: fullWidth ? "100%" : undefined,
    marginTop: 8,
  };
}

function Spinner({ lang }) {
  return (
    <div style={{ textAlign: "center", padding: "2.5rem 1rem", color: "#5b7a9a" }}>
      <Loader2 size={20} className="dkt-spin" /> {lang === "es" ? "Cargando…" : "Loading…"}
    </div>
  );
}

function ErrorPanel({ text }) {
  return (
    <PortalCard>
      <div style={{ display: "flex", alignItems: "center", gap: 10, color: "#991b1b" }}>
        <AlertCircle size={18} /> {text}
      </div>
    </PortalCard>
  );
}

function EmptyPanel({ text }) {
  return (
    <PortalCard>
      <div style={{ textAlign: "center", padding: "1rem 0", color: "#7d97b3", fontSize: ".88rem" }}>
        {text}
      </div>
    </PortalCard>
  );
}

function FullPageLoading() {
  return (
    <div style={{
      minHeight: "100vh", background: "#f4f7fb", display: "grid", placeItems: "center",
      fontFamily: "system-ui,-apple-system,Segoe UI,sans-serif", color: "#5b7a9a",
    }}>
      <div style={{ textAlign: "center" }}>
        <Loader2 size={32} className="dkt-spin" />
        <div style={{ marginTop: 12 }}>Loading your portal…</div>
      </div>
    </div>
  );
}

function FullPageError({ error, lang }) {
  return (
    <div style={{
      minHeight: "100vh", background: "#f4f7fb", display: "grid", placeItems: "center",
      padding: "2rem", fontFamily: "system-ui,-apple-system,Segoe UI,sans-serif",
    }}>
      <div style={{
        maxWidth: 460, background: "#fff", borderRadius: 14, padding: "2rem",
        textAlign: "center", boxShadow: "0 4px 12px rgba(0,0,0,.06)",
      }}>
        <div style={{
          width: 64, height: 64, borderRadius: 16, margin: "0 auto 1rem",
          background: "#fee2e2", border: "1px solid #ef4444",
          display: "grid", placeItems: "center",
        }}>
          <AlertCircle size={28} color="#ef4444" />
        </div>
        <h2 style={{ margin: "0 0 .5rem", color: "#0a1628" }}>
          {lang === "es" ? "No pudimos cargar tu portal" : "Couldn't load your portal"}
        </h2>
        <p style={{ color: "#5b7a9a", lineHeight: 1.55, margin: 0, fontSize: ".88rem" }}>
          {error}
        </p>
        <p style={{ color: "#7d97b3", lineHeight: 1.5, marginTop: 12, fontSize: ".78rem" }}>
          {lang === "es"
            ? "El link puede haber expirado o haber sido revocado. Contacta a tu agente para obtener uno nuevo."
            : "The link may have expired or been revoked. Contact your agent to get a new one."}
        </p>
      </div>
    </div>
  );
}

function fmtDate(iso, lang) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", { dateStyle: "medium" });
  } catch { return "—"; }
}

// Una FECHA DE CALENDARIO — la vigencia de una poliza, su renovacion, el
// vencimiento de una factura — no es un instante: es un dia. El servidor la
// guarda como medianoche UTC (`new Date('2026-08-15')` en policies.routes.ts),
// asi que `new Date(iso).toLocaleDateString()` la pasa por la zona horaria del
// navegador y en toda America la corre un dia HACIA ATRAS: la poliza que
// empieza el 15 se le enseña al cliente como el 14.
//
// Reportado el 2026-08-25: *"el portal del cliente no esta tomando bien la
// fecha de inicio de la poliza y renovacion, esta mostrando otras fechas que
// no es lo que dice la poliza"*. Es la regla que ya estaba escrita en
// CLAUDE.md — dia local, nunca UTC — que aqui no se habia aplicado.
//
// El dia se arma con las PARTES de la cadena, sin pasar por la zona horaria —
// ver `calendarDayOf` en utils.js, donde vive la regla y sus pruebas. Cuando
// devuelve null la marca lleva una hora de verdad: es un instante (una factura
// generada a las 8pm) y ese si tiene que verse en la hora local del que mira,
// asi que se cae al formateador normal.
function fmtDay(iso, lang) {
  if (!iso) return "—";
  const d = calendarDayOf(iso);
  if (!d) return fmtDate(iso, lang);
  return d.toLocaleDateString("en-US", { dateStyle: "medium" });
}

function fmtSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
